import { Component, EventEmitter, Input, Output, computed, inject, input, DestroyRef, ChangeDetectionStrategy } from "@angular/core";
import { MatCard } from "@angular/material/card";
import { MatIcon } from "@angular/material/icon";
import { Router } from "@angular/router";
import { TranslocoPipe } from "@jsverse/transloco";
import dayjs from "dayjs";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LOREN_ROUTES } from "../../../../shared/constants/loren-routes.constants";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";
import { MatAnchor, MatButton } from "@angular/material/button";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";
import { isDeepEqual } from "remeda";
import { forkJoin, switchMap } from "rxjs";
import { LorenGroupService } from "../../../loren-groups/services/loren-group-service/loren-group.service";
import { AsyncPipe } from "@angular/common";
import { getAssetLocation } from "../../../asset/asset.utils";


@Component({
  imports: [
    MatCard,
    MatIcon,
    TranslocoPipe,
    LorenDateModule,
    MatAnchor,
    MatButton,
    AsyncPipe
  ],
    selector: "loren-map-details-sidepanel",
    styleUrl: "./loren-map-details-sidepanel.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./loren-map-details-sidepanel.component.html"
})
export class LorenMapDetailsSidepanelComponent {
  private router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly groupService = inject(LorenGroupService);

  asset = input.required<LorenAsset>();
  location = computed(() => getAssetLocation(this.asset()))

  groups$ = toObservable(this.asset).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(asset => asset.belongsToGroups),
    distinctUntilChanged((previous, current) => isDeepEqual(previous, current)),
    switchMap(groups => forkJoin(groups.map(g => this.groupService.get(g)))),
    map(groups => groups.map(x => x.name)),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  @Input() mapHeight: string;
  @Output() closed = new EventEmitter<null>();

  assetUpdated = computed(() => this.calculateLastUpdateForAsset(this.asset()));

  navigateToAsset() {
    this.router.navigateByUrl(`${LOREN_ROUTES.ASSET}/${this.asset().id}`);
  }

  private calculateLastUpdateForAsset(asset: LorenAsset) {
    if (asset.updatedOn) {
      return asset.updatedOn;
    }
    return asset.metering
      .map((metering) => dayjs(metering.time))
      .reduce((latest, current) => {
        return current.isBefore(latest) ? current : latest;
      })
      .format();
  }
}
